import React, {ReactNode, useState, forwardRef} from 'react'
import {useIntl} from 'react-intl'
import {FormControl, InputGroup, InputRightElement, Input as ChakraInput, FormLabel, FormErrorMessage, InputLeftElement} from '@chakra-ui/react'
import {Icon} from '_uiComponents'

interface InputProps {
    name: string
    type: string
    label?: string | ReactNode
    placeholder?: string
    leftIcon?: React.ReactNode
    rightIcon?: React.ReactNode
    isRequired?: boolean
    value?: string
    errorMessage?: string
    isDisabled?: boolean
    isReadOnly?: boolean
    variant?: 'search' | 'newsletter' | (string & {})
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
}

/**
 * @name Input
 *
 * @component
 *
 * @description
 * A reusable input component with various customisation options.
 *
 * @param {string} name - The name attribute for the input element.
 * @param {string} type - The type attribute for the input element (e.g., "text", "password", "email").
 * @param {string | ReactNode} [label] - Optional label for the input element. Can be a string or a ReactNode.
 * @param {string} [placeholder] - Optional placeholder text for the input element.
 * @param {ReactNode} [leftIcon] - Optional icon to be displayed on the left side of the input element.
 * @param {ReactNode} [rightIcon] - Optional icon to be displayed on the right side of the input element.
 * @param {boolean} [isRequired] - Optional boolean to mark the input as required.
 * @param {string} [value] - Optional value for the input element.
 * @param {string} [errorMessage] - Optional error message to be displayed below the input element.
 * @param {boolean} [isDisabled] - Optional boolean to disable the input element.
 * @param {boolean} [isReadOnly] - Optional boolean to make the input element read-only.
 * @param {'search' | 'newsletter' | string} [variant] - Optional variant for the input element. Can be 'search', 'newsletter', or a custom string.
 * @param {(event: ChangeEvent<HTMLInputElement>) => void} [onChange] - Optional event handler for the input's change event.
 * @param {(event: FocusEvent<HTMLInputElement>) => void} [onBlur] - Optional event handler for the input's blur event.
 * @param {React.Ref<HTMLInputElement>} ref - Forwarded ref to the input element.
 *
 * @returns A styled input component with optional icons, label, and error message.
 *
 * @example
 * ```tsx
 * <Input
 * name="email"
 * type="email"
 * label="Email Address"
 * placeholder="Enter your email"
 * leftIcon={<EmailIcon />}
 * isRequired={true}
 * onChange={(e) => console.log(e.target.value)}
 * />
 * ```
 */
const Input = forwardRef<HTMLInputElement, InputProps>(
    (
        {name, type, label, placeholder, leftIcon, rightIcon, isRequired, value, errorMessage, variant, isDisabled, isReadOnly, onChange, onBlur, ...props},
        ref,
    ) => {
        const {formatMessage} = useIntl()
        const [isPasswordVisible, setIsPasswordVisible] = useState(false)

        return (
            <FormControl
                id={name}
                isRequired={isRequired}
                isInvalid={!!errorMessage}
                isDisabled={isDisabled}
                isReadOnly={isReadOnly}
                data-testid={`input-${name}`}
            >
                {label && <FormLabel>{label}</FormLabel>}

                <InputGroup>
                    {leftIcon && <InputLeftElement>{leftIcon}</InputLeftElement>}

                    <ChakraInput
                        id={name}
                        type={isPasswordVisible ? 'text' : type}
                        name={name}
                        placeholder={placeholder}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        ref={ref}
                        variant={variant}
                        {...props}
                    />

                    {type === 'password' && (
                        <InputRightElement
                            as="button"
                            aria-label={formatMessage({id: 'form.password.show', defaultMessage: 'Show password'})}
                            aria-pressed={isPasswordVisible}
                            onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                        >
                            <Icon name={isPasswordVisible ? 'IconEyeSlash' : 'IconEye'} aria-hidden />
                        </InputRightElement>
                    )}

                    {rightIcon && <InputRightElement>{rightIcon}</InputRightElement>}
                </InputGroup>
                {errorMessage && <FormErrorMessage>{errorMessage}</FormErrorMessage>}
            </FormControl>
        )
    },
)

Input.displayName = 'Input'

export {Input}
